import { useCallback, useEffect, useRef, useState } from "react";

import { TailoringCoverageTransitionSpinnerArt } from "./TailoringCoverageTransitionSpinnerArt";
import { TailoringCoverageTransitionDesktopArt } from "./TailoringCoverageTransitionDesktopArt";
import { TailoringCoverageTransitionMobileArt } from "./TailoringCoverageTransitionMobileArt";
import {
  TRANSITION_COMPLETE_ANIMATION,
  useHasSeenTransition,
} from "./useHasSeenTransition";
import { Modal, VisuallyHidden, useBreakpoint } from "@simplybusiness/mobius";

/**
 * Props for the TailoringCoverageTransition component
 */
export type TailoringCoverageTransitionProps = {
  /** Optional image URL (typically brand logo) to display at the top of the overlay */
  tradeImage?: string;
  /** Callback invoked when the spinner animation completes */
  onSpinnerEnd?: () => void;
  /** Callback invoked when the transition animation completes */
  onComplete?: () => void;
};

export const TailoringCoverageTransition = ({
  tradeImage,
  onSpinnerEnd,
  onComplete,
}: TailoringCoverageTransitionProps) => {
  const { isReady, hasSeen, setHasSeen } = useHasSeenTransition();
  const { up } = useBreakpoint();
  const isDesktop = up("md");

  // Track whether the modal should be open (closes after animation completes)
  const [isOpen, setIsOpen] = useState(true);

  const containerRef = useRef<HTMLDivElement>(null);
  const loaderRef = useRef<HTMLDivElement>(null);

  // Store callbacks in refs to avoid re-subscribing on every render
  const onCompleteRef = useRef(onComplete);
  const onSpinnerEndRef = useRef(onSpinnerEnd);
  onCompleteRef.current = onComplete;
  onSpinnerEndRef.current = onSpinnerEnd;

  const handleSpinnerAnimationEnd = useCallback((event: AnimationEvent) => {
    if (event.animationName === "prixClipFix") {
      onSpinnerEndRef.current?.();
    }
  }, []);

  const handleAnimationEnd = useCallback(
    (event: AnimationEvent) => {
      if (event.animationName === TRANSITION_COMPLETE_ANIMATION) {
        setHasSeen();
        setIsOpen(false);
        onCompleteRef.current?.();
      }
    },
    [setHasSeen],
  );

  // If user has seen the transition, call callbacks after a brief delay
  // Stage the callbacks to maintain consistent ordering with the full animation
  useEffect(() => {
    if (isReady && hasSeen) {
      const spinnerTimer = setTimeout(() => onSpinnerEndRef.current?.(), 800);
      const completeTimer = setTimeout(() => {
        setIsOpen(false);
        onCompleteRef.current?.();
      }, 1250);
      return () => {
        clearTimeout(spinnerTimer);
        clearTimeout(completeTimer);
      };
    }
  }, [isReady, hasSeen]);

  // Listen for animation completion
  useEffect(() => {
    if (!isReady || hasSeen) return;

    const container = containerRef.current;
    const loader = loaderRef.current;

    if (!container || !loader) return;

    // Listen on loader element for prixClipFix animation end (triggers at ~2000ms)
    loader.addEventListener("animationend", handleSpinnerAnimationEnd);

    // Listen on container for final completion
    container.addEventListener("animationend", handleAnimationEnd);

    // Fallback timeout in case animations fail to fire (CSS not loaded, browser quirk)
    // Total animation duration is ~4.5s, so use staged timeouts with generous buffer
    const spinnerFallbackTimer = setTimeout(
      () => onSpinnerEndRef.current?.(),
      3000,
    );
    const completeFallbackTimer = setTimeout(() => {
      setHasSeen();
      setIsOpen(false);
      onCompleteRef.current?.();
    }, 8000);

    return () => {
      loader.removeEventListener("animationend", handleSpinnerAnimationEnd);
      container.removeEventListener("animationend", handleAnimationEnd);
      clearTimeout(spinnerFallbackTimer);
      clearTimeout(completeFallbackTimer);
    };
  }, [
    isReady,
    hasSeen,
    handleSpinnerAnimationEnd,
    handleAnimationEnd,
    setHasSeen,
  ]);

  return (
    <Modal
      isOpen={isOpen && isReady}
      isFullScreen
      className="mobius-tailoring-coverage-transition__modal"
    >
      {hasSeen ? (
        <div
          ref={containerRef}
          className="mobius-tailoring-coverage-transition__fallback"
          data-testid="tailoring-coverage-transition-fallback"
        >
          <div className="mobius-tailoring-coverage-transition__loader" />
        </div>
      ) : (
        <div
          ref={containerRef}
          className="mobius-tailoring-coverage-transition__overlay"
          data-testid="tailoring-coverage-transition-overlay"
        >
          <div className="mobius-tailoring-coverage-transition__transition">
            <div className="mobius-tailoring-coverage-transition__content">
              {tradeImage && (
                <img
                  src={tradeImage}
                  alt="Illustration representing your business"
                  className="mobius-tailoring-coverage-transition__trade-image"
                />
              )}
              <VisuallyHidden>
                <p>We&apos;re tailoring recommendations just for you</p>
              </VisuallyHidden>
              {isDesktop ? (
                <TailoringCoverageTransitionDesktopArt />
              ) : (
                <TailoringCoverageTransitionMobileArt />
              )}
              <div
                ref={loaderRef}
                className="mobius-tailoring-coverage-transition__loader"
                data-testid="tailoring-coverage-transition-loader"
              >
                <TailoringCoverageTransitionSpinnerArt className="mobius-tailoring-coverage-transition__transition-icon" />
              </div>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
};
